import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  ArrowLeft,
  CalendarDays,
  MapPin,
  Building2,
  Users,
  BarChart3,
  Map,
  Bot,
  Headset,
  Pencil,
  Save,
  Globe,
  Tag,
  Image,
  Loader2,
  EyeOff,
  Megaphone,
} from "lucide-react";
import type { Event } from "@mvk/shared";
import { useAdminAuth } from "../auth/AdminAuthContext";
import { CuratorEventHub } from "./curator/CuratorEventHub";
import { adminEventsApi } from "../../services/adminEventsApi";
import { adminEventsService, tryAdminEventById } from "../../services/adminEventsService";
import { isApiEnabled } from "../../api/config";
import { ApiError } from "../../api/client";
import { AnnouncementModal } from "../components/AnnouncementModal";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
}

type EventForm = {
  title: string;
  industry: string;
  city: string;
  venueName: string;
  description: string;
};

export function AdminEventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [form, setForm] = useState<EventForm | null>(null);
  const [exhibitorsCount, setExhibitorsCount] = useState(0);
  const [visitorsCount, setVisitorsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingStub, setSavingStub] = useState(false);
  const [savingMapPublished, setSavingMapPublished] = useState(false);
  const [error, setError] = useState("");
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const { user } = useAdminAuth();

  const loadEvent = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");

    try {
      const fromApi = await tryAdminEventById(id);
      if (fromApi) {
        setEvent(fromApi);
        setForm({
          title: fromApi.title,
          industry: fromApi.industry,
          city: fromApi.city,
          venueName: fromApi.venueName,
          description: fromApi.description,
        });
        setExhibitorsCount(fromApi.companiesCount);
        setVisitorsCount(fromApi.visitorsCount);

        if (isApiEnabled()) {
          try {
            const [exhibitors] = await Promise.all([
              adminEventsApi.listExhibitors(id),
            ]);
            setExhibitorsCount(exhibitors.exhibitors.length);
          } catch {
            // keep counts from event row
          }
        }
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  if (user?.role === "curator") {
    return <CuratorEventHub />;
  }

  const [uploadingHero, setUploadingHero] = useState(false);
  const heroInputRef = React.useRef<HTMLInputElement>(null);

  const handleHeroUpload = async (file: File | null) => {
    if (!id || !file) return;
    if (!file.type.startsWith("image/")) {
      setError("Нужен файл изображения (JPG/PNG/WebP)");
      return;
    }
    if (file.size > 2_500_000) {
      setError("Обложка слишком большая (макс. ~2.5 МБ)");
      return;
    }

    setUploadingHero(true);
    setError("");
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("read_failed"));
        reader.readAsDataURL(file);
      });
      const { event: updated } = await adminEventsApi.uploadHero(id, dataUrl);
      setEvent(updated);
    } catch (err) {
      const body =
        err instanceof ApiError && err.body && typeof err.body === "object"
          ? (err.body as { error?: string }).error
          : undefined;
      setError(
        body === "tezam_write_failed"
          ? "Не удалось записать обложку в БД Tezam"
          : body === "hero_too_large"
            ? "Обложка слишком большая"
            : "Не удалось загрузить обложку",
      );
    } finally {
      setUploadingHero(false);
    }
  };

  const handleSave = async () => {
    if (!id || !form || !event) return;
    if (!isApiEnabled()) {
      setEditMode(false);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const updated = await adminEventsService.update(id, {
        title: form.title,
        industry: form.industry,
        city: form.city,
        venueName: form.venueName,
        description: form.description,
      });
      setEvent(updated);
      setEditMode(false);
    } catch (err) {
      setError(err instanceof ApiError ? "Не удалось сохранить" : "Ошибка сети");
    } finally {
      setSaving(false);
    }
  };

  const handleUnlockLocation = async () => {
    if (!id || !event) return;
    if (!isApiEnabled()) return;
    setSaving(true);
    setError("");
    try {
      const updated = await adminEventsService.update(id, { locationLocalOverride: false });
      setEvent(updated);
    } catch {
      setError("Не удалось снять фиксацию адреса");
    } finally {
      setSaving(false);
    }
  };

  const handleStubToggle = async (next: boolean) => {
    if (!id || !event) return;
    if (!isApiEnabled()) {
      setEvent({ ...event, stubMode: next });
      return;
    }
    setSavingStub(true);
    setError("");
    try {
      const updated = await adminEventsService.update(id, { stubMode: next });
      setEvent(updated);
    } catch {
      setError("Не удалось изменить заглушку");
    } finally {
      setSavingStub(false);
    }
  };

  const handleMapPublishedToggle = async (next: boolean) => {
    if (!id || !event) return;
    if (!isApiEnabled()) {
      setEvent({ ...event, mapPublished: next });
      return;
    }
    setSavingMapPublished(true);
    setError("");
    try {
      const updated = await adminEventsService.update(id, { mapPublished: next });
      setEvent(updated);
    } catch {
      setError("Не удалось изменить публикацию карты");
    } finally {
      setSavingMapPublished(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!event || !form) {
    return <div className="p-6 text-gray-500 text-[14px]">Мероприятие не найдено</div>;
  }

  const sections = [
    {
      label: "Экспоненты",
      value: exhibitorsCount,
      sub: "компаний зарегистрировано",
      icon: <Building2 size={18} className="text-violet-500" />,
      bg: "bg-violet-50",
      href: `/events/${id}/exhibitors`,
    },
    {
      label: "Доступы",
      value: visitorsCount.toLocaleString("ru-RU"),
      sub: "зарегистрировано",
      icon: <Users size={18} className="text-sky-500" />,
      bg: "bg-sky-50",
      href: `/events/${id}/access`,
    },
    {
      label: "Карта / стенды",
      value: exhibitorsCount,
      sub: "стендов на карте",
      icon: <Map size={18} className="text-emerald-500" />,
      bg: "bg-emerald-50",
      href: `/events/${id}/map`,
    },
    {
      label: "Аналитика",
      value: "—",
      sub: "статистика событий",
      icon: <BarChart3 size={18} className="text-indigo-500" />,
      bg: "bg-indigo-50",
      href: `/events/${id}/stats`,
    },
    {
      label: "Чат-бот",
      value: "—",
      sub: "детали и знания",
      icon: <Bot size={18} className="text-violet-500" />,
      bg: "bg-violet-50",
      href: `/events/${id}/support-bot`,
    },
    {
      label: "Ссылки поддержки",
      value: "—",
      sub: "внешние чаты",
      icon: <Headset size={18} className="text-orange-500" />,
      bg: "bg-orange-50",
      href: `/events/${id}/support`,
    },
  ];

  const statusLabels = { active: "Идёт сейчас", upcoming: "Предстоит", past: "Завершено" };
  const statusColors = {
    active: "bg-emerald-100 text-emerald-700",
    upcoming: "bg-blue-100 text-blue-700",
    past: "bg-gray-100 text-gray-600",
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      {showAnnouncement && id ? (
        <AnnouncementModal eventId={id} onClose={() => setShowAnnouncement(false)} />
      ) : null}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/events")}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-[20px] text-gray-900">{event.title}</h1>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${statusColors[event.status]}`}>
              {statusLabels[event.status]}
            </span>
            {event.stubMode && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                Заглушка
              </span>
            )}
          </div>
          <p className="text-[13px] text-gray-400 mt-0.5">
            {event.city} · {event.venueName} · {formatDate(event.startDate)} — {formatDate(event.endDate)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowAnnouncement(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-[13px] hover:bg-indigo-700"
            title="Рассылка в MAX и PWA"
          >
            <Megaphone size={14} />
            <span className="hidden sm:inline">Объявление</span>
          </button>
          <button
            onClick={() => {
              if (editMode) handleSave();
              else setEditMode(true);
            }}
            disabled={saving}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${
              editMode
                ? "bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                : "bg-white border border-gray-200 text-gray-700 hover:border-indigo-200 hover:text-indigo-600"
            }`}
          >
            {editMode ? (
              saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Сохранить</>
            ) : (
              <><Pencil size={14} /> Редактировать</>
            )}
          </button>
        </div>
      </div>

      {error && <p className="text-[13px] text-red-600">{error}</p>}

      <div className="relative h-48 rounded-2xl overflow-hidden bg-gray-100">
        {event.heroImage ? (
          <img src={event.heroImage} alt={event.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-[13px]">
            Обложка не загружена
          </div>
        )}
        {editMode && (
          <>
            <input
              ref={heroInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => void handleHeroUpload(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              onClick={() => heroInputRef.current?.click()}
              disabled={uploadingHero}
              className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer hover:bg-black/50 transition-colors"
            >
              <div className="flex items-center gap-2 text-white text-[13px]">
                {uploadingHero ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Image size={18} />
                )}
                {uploadingHero ? "Загрузка…" : "Заменить обложку"}
              </div>
            </button>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {sections.map((s) => (
          <button
            key={s.label}
            onClick={() => navigate(s.href)}
            className="bg-white rounded-xl border border-gray-100 p-4 text-left hover:border-indigo-100 hover:shadow-sm transition-all"
          >
            <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center mb-3`}>
              {s.icon}
            </div>
            <div className="text-[18px] text-gray-900">{s.value}</div>
            <div className="text-[12px] text-gray-500 mt-0.5">{s.label}</div>
            <div className="text-[11px] text-gray-400">{s.sub}</div>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h2 className="text-[14px] text-gray-900 mb-4">Основная информация</h2>
        <div className="mb-5 rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <EyeOff size={16} className="text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[13px] text-gray-900 font-medium">Заглушка в приложении</p>
                <p className="text-[12px] text-gray-500 mt-0.5 leading-snug">
                  Посетители видят экран «информация скоро будет доступна»; каталог и карта скрыты.
                  Удобно, пока билеты уже продаются, а контент ещё не готов.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(event.stubMode)}
                disabled={savingStub}
                onClick={() => void handleStubToggle(!event.stubMode)}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
                  event.stubMode ? "bg-amber-500" : "bg-gray-200"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    event.stubMode ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        <div className="mb-5 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <MapPin size={16} className="text-indigo-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[13px] text-gray-900 font-medium">Карта опубликована</p>
                <p className="text-[12px] text-gray-500 mt-0.5 leading-snug">
                  Выключите, пока схема зала наполняется — в приложении будет сервисное сообщение
                  вместо незавершённой карты.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={event.mapPublished !== false}
                disabled={savingMapPublished}
                onClick={() => void handleMapPublishedToggle(!(event.mapPublished !== false))}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
                  event.mapPublished !== false ? "bg-indigo-500" : "bg-gray-200"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    event.mapPublished !== false ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[
            { key: "title" as const, label: "Название", icon: <Tag size={14} />, col: "col-span-2" },
            { key: "industry" as const, label: "Отрасль", icon: <Tag size={14} /> },
            { key: "city" as const, label: "Город", icon: <MapPin size={14} /> },
            { key: "venueName" as const, label: "Площадка", icon: <MapPin size={14} /> },
          ].map((field) => (
            <div key={field.key} className={field.col}>
              <label className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">
                {field.icon} {field.label}
              </label>
              {editMode ? (
                <input
                  value={form[field.key]}
                  onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                />
              ) : (
                <div className="text-[13px] text-gray-900 py-2">{form[field.key]}</div>
              )}
            </div>
          ))}
          <div className="col-span-2 -mt-2">
            <p className="text-[11px] text-gray-400 leading-snug">
              {event.locationLocalOverride
                ? "Город и площадка зафиксированы в админке — реплика их не перезапишет."
                : "После сохранения город и площадка не будут перезаписываться данными из реплики."}
            </p>
            {event.locationLocalOverride && (
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleUnlockLocation()}
                className="mt-1 text-[11px] text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
              >
                Снова брать город из реплики
              </button>
            )}
          </div>

          <div>
            <label className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">
              <Globe size={14} /> Сайт
            </label>
            <div className="text-[13px] text-gray-900 py-2">—</div>
          </div>
          <div>
            <label className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">
              <CalendarDays size={14} /> Дата начала
            </label>
            <div className="text-[13px] text-gray-900 py-2">{formatDate(event.startDate)}</div>
          </div>
          <div>
            <label className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">
              <CalendarDays size={14} /> Дата окончания
            </label>
            <div className="text-[13px] text-gray-900 py-2">{formatDate(event.endDate)}</div>
          </div>

          <div className="col-span-2">
            <label className="text-[11px] text-gray-400 mb-1 block">Описание</label>
            {editMode ? (
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
              />
            ) : (
              <div className="text-[13px] text-gray-700 leading-relaxed">{form.description}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
